// Core hook for boutique onboarding – manages form state, Firestore CRUD,
// image uploads, step validation, and draft persistence.

import { useState, useEffect, useCallback } from 'react';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  GeoPoint,
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
} from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../config/firebase';
import { ensureBrowserCompatibleImage, resizeImageFile, IMAGE_LIMITS } from '../lib/imageUtils';
import { useAuth } from './useAuth';
import {
  OnboardingFormData,
  DEFAULT_FORM_DATA,
  ONBOARDING_STEPS,
  BusinessDocument,
  ClientSafeLegal,
} from '../types/business';

// ── Step validators ─────────────────────────────────────────────────
export function validateStep(step: number, data: OnboardingFormData): string[] {
  const errors: string[] = [];

  switch (step) {
    case 1:
      if (!data.name.trim()) errors.push('Boutique name is required');
      if (data.name.trim().length < 2) errors.push('Name must be at least 2 characters');
      if (!data.description.trim()) errors.push('Description is required');
      if (data.description.trim().length < 20) errors.push('Description must be at least 20 characters');
      if (data.categories.length === 0) errors.push('Select at least one category');
      break;

    case 2:
      if (!data.address.street.trim()) errors.push('Street address is required');
      if (!data.address.city.trim()) errors.push('City is required');
      if (!data.address.state) errors.push('State is required');
      if (!data.address.zipCode.trim()) errors.push('ZIP code is required');
      if (!/^\d{5}(-\d{4})?$/.test(data.address.zipCode.trim())) errors.push('Enter a valid ZIP code');
      if (!data.phone.trim()) errors.push('Store phone number is required for delivery couriers');
      if (data.phone.trim() && data.phone.replace(/\D/g, '').length < 10) errors.push('Enter a valid 10-digit phone number');
      break;

    case 3:
      // At least one day must have hours
      const hasAnyHours = Object.values(data.hours).some(h => h !== null);
      if (!hasAnyHours) errors.push('Set hours for at least one day');
      // Validate open < close for each day
      Object.entries(data.hours).forEach(([day, h]) => {
        if (h && h.open >= h.close) {
          errors.push(`${day}: closing time must be after opening time`);
        }
      });
      if (data.prepTimeEstimate < 5 || data.prepTimeEstimate > 120)
        errors.push('Prep time must be 5-120 minutes');
      break;

    case 4:
      // Fulfillment method is always 'fash_drivers' (Uber Direct) — no validation needed
      break;

    case 5:
      if (data.priceRange.min < 0) errors.push('Minimum price cannot be negative');
      if (data.priceRange.max <= data.priceRange.min)
        errors.push('Maximum price must be greater than minimum');
      if (data.inventorySettings.lowStockThreshold < 1)
        errors.push('Low stock threshold must be at least 1');
      break;

    case 6:
      if (!data.legal.businessName.trim()) errors.push('Legal business name is required');
      if (!data.legal.businessType) errors.push('Business type is required');
      if (!data.legal.returnPolicy.type) errors.push('Return policy is required');
      if (data.legal.returnPolicy.type === 'custom' && !data.legal.returnPolicy.description.trim())
        errors.push('Custom return policy description is required');
      break;

    case 7:
      // All fields optional – no validation needed
      break;

    case 8:
      // Final review – re-validate everything critical
      const criticalSteps = [1, 2, 3, 4, 5, 6];
      criticalSteps.forEach(s => {
        const stepErrors = validateStep(s, data);
        if (stepErrors.length > 0) {
          const stepInfo = ONBOARDING_STEPS[s - 1];
          errors.push(`Step ${s} (${stepInfo.label}) has issues`);
        }
      });
      break;
  }

  return errors;
}

// ── Main hook ───────────────────────────────────────────────────────
export function useBoutiqueOnboarding() {
  const { user, userProfile } = useAuth();
  const [formData, setFormData] = useState<OnboardingFormData>(DEFAULT_FORM_DATA);
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [hasExistingDraft, setHasExistingDraft] = useState(false);

  // ── Load existing draft on mount ──────────────────────────────────
  useEffect(() => {
    async function loadDraft() {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // The tax record is read through a callable because the TIN is
        // encrypted at rest and only ever returned masked.
        const getTaxInfo = httpsCallable<Record<string, never>, ClientSafeLegal>(
          functions,
          'getBusinessTaxInfo',
        );
        const [bizDoc, taxInfo] = await Promise.all([
          getDoc(doc(db, 'businesses', user.uid)),
          getTaxInfo({}).then((r) => r.data).catch(() => null),
        ]);
        if (bizDoc.exists()) {
          const data = bizDoc.data();
          setBusinessId(user.uid);
          setHasExistingDraft(true);

          // If already completed onboarding, go to step 8 (review)
          if (data.status === 'active') {
            setCurrentStep(8);
          } else {
            setCurrentStep(data.onboardingStep || 1);
          }

          // Hydrate form from saved data
          setFormData(prev => ({
            ...prev,
            name: data.name || '',
            description: data.description || '',
            categories: data.categories || [],
            logoPreview: data.logo || '',
            coverPreview: data.coverImage || '',
            address: data.address || prev.address,
            phone: data.phone || '',
            deliveryRadius: data.deliveryRadius || 5,
            hours: data.hours || prev.hours,
            timezone: data.timezone || prev.timezone,
            prepTimeEstimate: data.prepTimeEstimate || 30,
            fulfillment: data.fulfillment || prev.fulfillment,
            inventorySettings: data.inventorySettings || prev.inventorySettings,
            priceRange: data.priceRange || prev.priceRange,
            collections: data.collections || [],
            legal: taxInfo ?? prev.legal,
            socialMedia: data.socialMedia || prev.socialMedia,
            brandColor: data.brandColor || '#C084FC',
          }));
        }
      } catch (err: any) {
        console.error('Error loading onboarding draft:', err);
        setError('Failed to load your saved progress');
      } finally {
        setLoading(false);
      }
    }

    loadDraft();
  }, [user]);

  const uploadImage = useCallback(async (
    file: File,
    path: string,
    kind: 'logo' | 'cover',
  ): Promise<string> => {
    const compatible = await ensureBrowserCompatibleImage(file);
    const { maxWidth, maxHeight } = IMAGE_LIMITS[kind];
    const resized = await resizeImageFile(compatible, maxWidth, maxHeight);
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, resized);
    return getDownloadURL(snapshot.ref);
  }, []);

  // ── Save current step to Firestore ────────────────────────────────
  const saveDraft = useCallback(async (step: number, data: OnboardingFormData) => {
    if (!user) throw new Error('Must be signed in');

    setSaving(true);
    setError(null);

    try {
      const docId = user.uid;
      let logoUrl = data.logoPreview;
      let coverUrl = data.coverPreview;

      // Upload images if new files were selected
      if (data.logoFile) {
        logoUrl = await uploadImage(
          data.logoFile,
          `business-logos/${docId}/logo`,
          'logo',
        );
      }
      if (data.coverFile) {
        coverUrl = await uploadImage(
          data.coverFile,
          `business-logos/${docId}/cover`,
          'cover',
        );
      }

      // Build the address string
      const formattedAddress = [
        data.address.street,
        data.address.unit,
        data.address.city,
        data.address.state,
        data.address.zipCode,
      ].filter(Boolean).join(', ');

      const docData: Partial<BusinessDocument> = {
        // Step 1
        name: data.name.trim(),
        description: data.description.trim(),
        categories: data.categories,
        logo: logoUrl,
        coverImage: coverUrl,

        // Step 2
        address: data.address,
        phone: data.phone.replace(/\D/g, '').replace(/^(\d{10})$/, '+1$1'),
        formattedAddress,
        deliveryRadius: data.deliveryRadius,
        // Coordinates captured from Places Autocomplete. When the merchant
        // typed the address manually, this stays null and the checkout
        // orchestrator geocodes + persists it server-side on the first order.
        location:
          typeof data.address.lat === 'number' && typeof data.address.lng === 'number'
            ? { lat: data.address.lat, lng: data.address.lng }
            : null,

        // Step 3
        hours: data.hours,
        timezone: data.timezone,
        prepTimeEstimate: data.prepTimeEstimate,

        // Step 4
        fulfillment: data.fulfillment,

        // Step 5
        inventorySettings: data.inventorySettings,
        priceRange: data.priceRange,
        collections: data.collections,

        // Step 6 (legal) is written to the owner-only private subcollection

        // Step 7
        socialMedia: data.socialMedia,
        brandColor: data.brandColor,

        // System
        ownerId: user.uid,
        status: 'onboarding',
        onboardingStep: step,
        active: false,
        updatedAt: serverTimestamp() as any,
      };

      if (!hasExistingDraft) {
        // First save – create document
        await setDoc(doc(db, 'businesses', docId), {
          ...docData,
          featured: false,
          rating: 0,
          reviewCount: 0,
          createdAt: serverTimestamp(),
        });
        setHasExistingDraft(true);
        setBusinessId(docId);

        // Link business to user profile
        await updateDoc(doc(db, 'users', user.uid), {
          businessId: docId,
          onboardingComplete: false,
        });
      } else {
        // Subsequent saves – update existing
        await updateDoc(doc(db, 'businesses', docId), docData);
      }

      // The tax record goes through a callable so the TIN is encrypted before
      // storage and a W-9 signature is invalidated if the details behind it
      // change. Writing it from here would bypass both.
      const saveTaxInfo = httpsCallable(functions, 'saveBusinessTaxInfo');
      await saveTaxInfo(data.legal);

      // Clear file references after upload
      if (data.logoFile || data.coverFile) {
        setFormData(prev => ({
          ...prev,
          logoFile: null,
          logoPreview: logoUrl,
          coverFile: null,
          coverPreview: coverUrl,
        }));
      }
    } catch (err: any) {
      console.error('Error saving onboarding data:', err);
      setError(err.message || 'Failed to save. Please try again.');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [user, hasExistingDraft, uploadImage]);

  // ── Navigate to next step ─────────────────────────────────────────
  const nextStep = useCallback(async () => {
    const errors = validateStep(currentStep, formData);
    if (errors.length > 0) {
      setError(errors[0]);
      return false;
    }

    try {
      await saveDraft(currentStep + 1, formData);
      setCurrentStep(prev => Math.min(prev + 1, ONBOARDING_STEPS.length));
      setError(null);
      return true;
    } catch {
      return false;
    }
  }, [currentStep, formData, saveDraft]);

  // ── Navigate to previous step ─────────────────────────────────────
  const prevStep = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError(null);
  }, []);

  // ── Jump to specific step ─────────────────────────────────────────
  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= ONBOARDING_STEPS.length) {
      setCurrentStep(step);
      setError(null);
    }
  }, []);

  // ── Activate the boutique (final step) ────────────────────────────
  const activateBoutique = useCallback(async () => {
    if (!user) throw new Error('Must be signed in');

    // Validate all required steps
    const allErrors = validateStep(8, formData);
    if (allErrors.length > 0) {
      setError(allErrors[0]);
      return false;
    }

    setSaving(true);
    setError(null);

    try {
      // Save final data and activate
      await saveDraft(8, formData);

      // Flip status to active
      await updateDoc(doc(db, 'businesses', user.uid), {
        status: 'active',
        active: true,
        onboardingStep: 8,
        updatedAt: serverTimestamp(),
      });

      // Mark user profile as onboarding complete
      await updateDoc(doc(db, 'users', user.uid), {
        onboardingComplete: true,
      });

      return true;
    } catch (err: any) {
      console.error('Error activating boutique:', err);
      setError(err.message || 'Failed to activate boutique');
      return false;
    } finally {
      setSaving(false);
    }
  }, [user, formData, saveDraft]);

  // ── Update a field in form data ───────────────────────────────────
  const updateField = useCallback(<K extends keyof OnboardingFormData>(
    field: K,
    value: OnboardingFormData[K]
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  }, []);

  // ── Compute completion state per step ─────────────────────────────
  const stepCompletion = ONBOARDING_STEPS.map((_, idx) => {
    const stepNum = idx + 1;
    return {
      step: stepNum,
      isComplete: validateStep(stepNum, formData).length === 0,
      errors: validateStep(stepNum, formData),
    };
  });

  const isStepValid = (step: number) => validateStep(step, formData).length === 0;

  return {
    // State
    formData,
    currentStep,
    saving,
    loading,
    error,
    businessId,
    hasExistingDraft,
    stepCompletion,

    // Actions
    updateField,
    setFormData,
    nextStep,
    prevStep,
    goToStep,
    saveDraft,
    activateBoutique,
    isStepValid,
    setError,
  };
}
